import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import dynamic from 'next/dynamic';
import * as Sentry from '@sentry/nextjs';
import SinglePost from '@/components/entry-templates';
import PublicLayout from '@/components/frontend/PublicLayout';
import { fetchEntry, fetchNavigations } from '@/lib/content-api';
import resolveEntryStaticData from '@/lib/content-api/entryDataResolver';
import { ExtendedGetServerSidePropsContext } from '@/lib/content-api/types';
import { Post } from '@/types';
import { isContentPreview } from '@/lib/content-api/utils';

const Loader = dynamic(() => import('@/components/back-office/common/Loading'));
const ErrorPage = dynamic(
  () => import('@/components/entry-templates/ErrorPage'),
);

interface Props {
  data: Post | null;
}

const getFallbackHero = (locale?: string) => {
  const hero = {
    __name: 'acf/hero',
    data: {
      image: {
        src: 'https://www.mhy.fi/wp-content/uploads/sites/3/2022/04/metsatilat_rahakapy_tumma.jpg',
        alt: 'Rahakäpy puussa',
      },
      heading: 'Metsätilat – osta metsää ja kiinteistöjä ',
      text: '{{kohteet}} metsä- ja kiinteistökohdetta odottaa. ',
      buttons: null,
    },
  };

  if (locale === 'sv') {
    hero.data.heading = 'Skogsfastigheter – köp skog och fastigheter';
    hero.data.text =
      '{{kohteet}} skogs- och fastighetsobjekt väntar på dig.\r\n';
  }

  if (locale === 'en') {
    hero.data.heading = 'Metsätilat – buy forest properties';
    hero.data.text = '{{kohteet}} forest properties await.\r\n';
  }

  return hero;
};

const Buy = ({ data }: Props) => {
  if (typeof data === 'undefined') {
    return <Loader />;
  }
  try {
    if (data) {
      return <SinglePost post={data} />;
    }
    // TODO: What now? Should not get here, but this is a must have page. Render error? Stay awaiting for the service somehow? Redirect?
    return null;
  } catch (error) {
    console.error('Entry rendering failed: ', error);
    Sentry.captureException(error);
    return <ErrorPage status="500" subHeading="Something went quite wrong" />;
  }
};

Buy.layout = PublicLayout;

export const getServerSideProps = async (
  context: ExtendedGetServerSidePropsContext,
) => {
  const { locale, preview, previewData } = context;

  try {
    const dataP = fetchEntry({
      slug: 'osta',
      lang: locale,
      previewData,
    }).then((p) => resolveEntryStaticData(p, context));

    const navigationsP = fetchNavigations({
      lang: locale,
      previewData,
    });

    const stringTranslationsP = serverSideTranslations(locale || 'fi', [
      'frontoffice',
      'backoffice',
      'tilavahti',
      'hasura_errors',
    ]);

    const [data, navigations, stringTranslations] = await Promise.all([
      dataP,
      navigationsP,
      stringTranslationsP,
    ]);

    if (!data) {
      // Site must have a 'buy' page, so invent the minimum viable post
      console.warn('No CMS data was available for Buy (/osta) -route');
      const minimumPost = await resolveEntryStaticData(
        {
          template: 'listings-page',
          post_type: 'page',
          fields: [getFallbackHero(locale)],
        },
        context,
      );

      if (!minimumPost) {
        console.error('Error generating fields');
        Sentry.captureMessage('Error generating fields');
      }
      return {
        props: {
          ...(stringTranslations || {}),
          data: minimumPost,
          navigations: navigations || null,
        },
      };
    }

    const {
      meta = null,
      translations: entryTranslations = null,
      ...post
    } = data;

    return {
      props: {
        ...stringTranslations,
        entryTranslations,
        navigations: navigations || null,
        metadata: {
          ...meta,
          url: post.url,
          canonical: meta?.canonical || post.url,
        },
        data: post,
        isPreview: !!preview && isContentPreview(previewData),
        isAdminMode: !!preview && !isContentPreview(previewData),
      },
    };
  } catch (err) {
    console.error('Error serving Osta data', err);
    Sentry.captureException(err);
    return {
      props: {
        data: null,
        navigations: null,
        listings: [],
      },
    };
  }
};

export default Buy;
